import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export type AppSettings = {
  success: boolean;
  registration: boolean;
  phone: {
    countries: DataItem[];
    verification: boolean;
  };

  limit: {
    marketplace: number;
    alerts: number;
    reference: number;
    active_orders: number;
  };

  user_types: DataItem[];
  oauth?: {
    google?: { enabled?: boolean };
    facebook?: { enabled?: boolean };
  };
};

type AppSettingsResponse = Omit<AppSettings, 'limit'> & {
  limit: {
    marketplace: number | string | null | undefined;
    alerts: number | string | null | undefined;
    reference: number | string | null | undefined;
    active_orders: number | string | null | undefined;
  };
};

/**
 * Error thrown when the backend returns an invalid settings payload.
 */
export class InvalidAppSettingsResponseError extends Error {
  constructor() {
    super('Invalid app settings response payload.');
    this.name = 'InvalidAppSettingsResponseError';
  }
}

/**
 * Returns the backend base URL with trailing slashes removed.
 */
function getBackendBaseUrl(): string {
  const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || '';

  return baseUrl.replace(/\/+$/, '');
}

/**
 * Converts a backend value to a number or returns a fallback value.
 *
 * Preserves negative finite numbers to match legacy coercion behaviour.
 */
function toNumberOrFallback(value: number | string | null | undefined, fallback: number): number {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && 0 !== parsedValue ? parsedValue : fallback;
}

/**
 * Fetches application settings through a public request.
 *
 * The endpoint can be guest-only in some environments, so the request
 * intentionally avoids sending cookies and auth-related interceptors.
 */
export async function getAppSettings(): Promise<AppSettings> {
  const response = await axios.get<AppSettingsResponse>(`${getBackendBaseUrl()}/api/v1/settings`, {
    timeout: 15000,
    withCredentials: false,
  });

  if (!response?.data || 'object' !== typeof response.data || !response.data.limit) {
    throw new InvalidAppSettingsResponseError();
  }

  const normalisedLimit = {
    marketplace: toNumberOrFallback(response.data.limit.marketplace, 10),
    active_orders: toNumberOrFallback(response.data.limit.active_orders, 5),
    alerts: toNumberOrFallback(response.data.limit.alerts, 10),
    reference: toNumberOrFallback(response.data.limit.reference, 6),
  };

  return {
    ...response.data,
    limit: normalisedLimit,
  };
}

export const getAppSettingsQueryKey = () => ['common', 'config'] as const;

export const initialDataSettings: AppSettings = {
  success: true,
  registration: false,
  phone: {
    countries: [
      { id: 'PH', value: {}, label: 'Philippines' },
      { id: 'AU', value: {}, label: 'Australia' },
      { id: 'KH', value: {}, label: 'Cambodia' },
      { id: 'HK', value: {}, label: 'Hong Kong' },
      { id: 'ID', value: {}, label: 'Indonesia' },
      { id: 'KW', value: {}, label: 'Kuwait' },
      { id: 'LA', value: {}, label: 'Laos' },
      { id: 'MY', value: {}, label: 'Malaysia' },
      { id: 'NL', value: {}, label: 'Netherlands' },
      { id: 'QA', value: {}, label: 'Qatar' },
      { id: 'SA', value: {}, label: 'Saudi Arabia' },
      { id: 'SG', value: {}, label: 'Singapore' },
      { id: 'KR', value: {}, label: 'South Korea' },
      { id: 'ES', value: {}, label: 'Spain' },
      { id: 'CH', value: {}, label: 'Switzerland' },
      { id: 'TW', value: {}, label: 'Taiwan' },
      { id: 'TH', value: {}, label: 'Thailand' },
      { id: 'UA', value: {}, label: 'Ukraine' },
      { id: 'AE', value: {}, label: 'United Arab Emirates' },
      { id: 'GB', value: {}, label: 'United Kingdom' },
      { id: 'US', value: {}, label: 'United States' },
      { id: 'VN', value: {}, label: 'Vietnam' },
    ],
    verification: false,
  },

  limit: {
    marketplace: 10,
    alerts: 10,
    reference: 6,
    active_orders: 5,
  },

  user_types: [
    { id: '0', label: 'Other', value: 'other' },
    { id: '1', label: 'Farmer', value: 'farmer' },
    { id: '2', label: 'Cooperative', value: 'cooperative' },
    { id: '3', label: 'Rice Mill', value: 'rice_mill' },
    { id: '4', label: 'Trader', value: 'trader' },
    { id: '5', label: 'Distributor', value: 'distributor' },
    { id: '6', label: 'Retailer', value: 'retailer' },
    { id: '7', label: 'Wholesaler', value: 'wholesaler' },
    { id: '8', label: 'Restaurant', value: 'restaurant' },
    { id: '9', label: 'Hotel', value: 'hotel' },
    { id: '10', label: 'LGU', value: 'lgu' },
    { id: '11', label: 'Other', value: 'other' },
  ],
};

export function useGetAppSettingsQuery(enabled = true) {
  return useQuery({
    queryKey: getAppSettingsQueryKey(),
    queryFn: getAppSettings,
    enabled: enabled,
    refetchOnWindowFocus: true,
  });
}
